import { Link } from "@tanstack/react-router";
import { type Constraints, type RuleGroup, durationLabel } from "@trackmark/core";
import { RuleBuilder } from "./RuleBuilder.js";
import styles from "./Playlists.module.css";

/**
 * Playlists: the index, the generator (§J15) and the result (design/README.md §10).
 *
 * §J15: *"Reuse the dynamic-list rule builder wholesale for the filter tree, then a constraints
 * block… A live match count above the Generate button showing what the filter alone returns,
 * before constraints bind — that is what makes `41 of 50 · max 2 per artist is the binding
 * constraint` legible instead of mysterious."*
 *
 * That ordering is the whole design of this screen. You see how many songs the filter finds, THEN
 * you see how many survived the constraints, so the gap between the two numbers has a cause you
 * already know about.
 *
 * And the result never lies by omission: **never a silent short list.**
 */

export interface PlaylistRow {
  id: number;
  name: string;
  itemCount: number;
  targetCount: number | null;
  updatedAt: string;
}

export interface GeneratedTrack {
  id: number;
  title: string;
  artist: string;
  album: string;
  albumId: number;
  score: number;
  durationMs: number | null;
}

export interface GenerateResult {
  requested: number;
  durationMs: number;
  poolSize: number;
  report: {
    binding: string | null;
    shortBy: number;
    relaxed: { name: string; to: number; would: number } | null;
  };
  tracks: GeneratedTrack[];
  /** The sentence, built server-side from the pick that produced it. */
  sentence: string;
}

export interface PlaylistsProps {
  playlists: PlaylistRow[];
  onDelete: (id: number) => void;

  building: boolean;
  onNew: () => void;
  onCloseBuilder: () => void;

  name: string;
  onName: (name: string) => void;
  rules: RuleGroup;
  onRules: (rules: RuleGroup) => void;
  hints: { excludes: number }[];
  /** What the FILTER alone returns, before constraints bind (§J15). */
  filterMatches: number | null;

  constraints: Constraints;
  onConstraints: (next: Constraints) => void;

  result: GenerateResult | null;
  generating: boolean;
  onGenerate: () => void;
  /** Applies the relaxation the report priced, then regenerates. */
  onRelax: () => void;
  onKeep: () => void;
  saving: boolean;
}

const dateLabel = (iso: string) => {
  const d = new Date(iso);
  const M = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  return `${String(d.getUTCDate()).padStart(2, "0")} ${M[d.getUTCMonth()]} ${String(d.getUTCFullYear() % 100).padStart(2, "0")}`;
};

const trackLength = (ms: number | null) =>
  ms === null ? "—" : `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, "0")}`;

export function Playlists(props: PlaylistsProps) {
  if (!props.building) return <PlaylistIndex {...props} />;
  return <Generator {...props} />;
}

function PlaylistIndex({ playlists, onDelete, onNew }: PlaylistsProps) {
  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div className={styles.title}>Playlists</div>
        <button type="button" className={styles.primary} data-testid="new-playlist" onClick={onNew}>
          + New playlist
        </button>
      </div>

      {playlists.length === 0 ? (
        <div className={styles.empty} data-testid="playlists-empty">
          <div className={styles.emptyTitle}>No playlists yet</div>
          <div className={styles.emptyHint}>A PLAYLIST IS A LIST THAT STOPPED CHANGING. GENERATE ONE FROM A FILTER.</div>
        </div>
      ) : (
        // §J13 again: the same table, not a card grid.
        <div className={styles.table} data-testid="playlists-table">
          <div className={styles.tableHead}>
            <div>NAME</div>
            <div style={{ textAlign: "right" }}>TRACKS</div>
            <div style={{ textAlign: "right" }}>ASKED FOR</div>
            <div style={{ textAlign: "right" }}>CREATED</div>
            <div />
          </div>
          {playlists.map((p) => (
            <div key={p.id} className={styles.tableRow} data-testid="playlist-row">
              {/* The name is the way in. A row you can only delete is a row that has forgotten
                  what it is for. */}
              <Link
                to="/playlist/$playlistId"
                params={{ playlistId: String(p.id) }}
                className={styles.rowName}
                data-testid="playlist-link"
              >
                {p.name}
              </Link>
              <div className={styles.rowCount}>{p.itemCount.toLocaleString()}</div>
              {/* Kept beside the count, because "41" alone hides that 50 was asked for. */}
              <div className={styles.rowMeta}>{p.targetCount ?? "—"}</div>
              <div className={styles.rowMeta}>{dateLabel(p.updatedAt)}</div>
              <button
                type="button"
                className={styles.rowRemove}
                aria-label={`Delete ${p.name}`}
                onClick={() => onDelete(p.id)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Generator({
  name,
  onName,
  rules,
  onRules,
  hints,
  filterMatches,
  constraints,
  onConstraints,
  result,
  generating,
  onGenerate,
  onRelax,
  onKeep,
  onCloseBuilder,
  saving,
}: PlaylistsProps) {
  const set = (patch: Partial<Constraints>) => onConstraints({ ...constraints, ...patch });

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <input
          className={styles.nameInput}
          value={name}
          aria-label="Playlist name"
          placeholder="Untitled playlist"
          data-testid="playlist-name"
          onChange={(event) => onName(event.target.value)}
        />
        <button type="button" className={styles.back} onClick={onCloseBuilder}>
          ← All playlists
        </button>
      </div>

      <div className={styles.columns}>
        <div className={styles.left}>
          <h2 className={styles.sectionTitle}>Filter</h2>
          <RuleBuilder value={rules} onChange={onRules} hints={hints} />

          <h2 className={styles.sectionTitle}>Constraints</h2>
          <div className={styles.constraints} data-testid="constraints">
            <Field label="How many tracks">
              <input
                type="number"
                className={styles.number}
                aria-label="How many tracks"
                data-testid="limit"
                value={constraints.limit}
                min={1}
                max={500}
                onChange={(event) => set({ limit: Number(event.target.value) })}
              />
            </Field>
            <Field label="Max per artist">
              <NullableNumber
                label="Max per artist"
                testId="max-per-artist"
                value={constraints.maxPerArtist ?? null}
                onChange={(maxPerArtist) => set({ maxPerArtist })}
              />
            </Field>
            <Field label="Max per album">
              <NullableNumber
                label="Max per album"
                testId="max-per-album"
                value={constraints.maxPerAlbum ?? null}
                onChange={(maxPerAlbum) => set({ maxPerAlbum })}
              />
            </Field>
            <Field label="Minimum score">
              <NullableNumber
                label="Minimum score"
                testId="min-score"
                value={constraints.minScore ?? null}
                onChange={(minScore) => set({ minScore })}
              />
            </Field>
            <Field label="Exclude played within (days)">
              <NullableNumber
                label="Exclude played within"
                testId="exclude-played"
                value={constraints.excludePlayedWithinDays ?? null}
                onChange={(excludePlayedWithinDays) => set({ excludePlayedWithinDays })}
              />
            </Field>
            <Field label="Order">
              <select
                className={styles.select}
                aria-label="Order"
                value={constraints.order ?? "score"}
                onChange={(event) => set({ order: event.target.value as "score" | "shuffle" })}
              >
                <option value="score">By score</option>
                <option value="shuffle">Shuffle</option>
              </select>
            </Field>
            {constraints.order === "shuffle" && (
              <Field label="Seed">
                {/* Exposed because §J15 lists it: a shuffle you cannot reproduce is not a recipe. */}
                <input
                  type="number"
                  className={styles.number}
                  aria-label="Seed"
                  data-testid="seed"
                  value={constraints.seed ?? 1}
                  onChange={(event) => set({ seed: Number(event.target.value) })}
                />
              </Field>
            )}
          </div>

          {/* §J15's live count, ABOVE Generate: what the filter alone returns, before constraints
              bind. Without it the gap between 50 asked and 41 got has no visible cause. */}
          <div className={styles.generateBar}>
            <div className={styles.filterCount} data-testid="filter-matches">
              {filterMatches === null ? "—" : filterMatches.toLocaleString()}
              <span className={styles.filterLabel}>SONGS MATCH THE FILTER, BEFORE CONSTRAINTS</span>
            </div>
            <button
              type="button"
              className={styles.primary}
              data-testid="generate"
              disabled={generating}
              onClick={onGenerate}
            >
              {generating ? "Generating…" : "Generate →"}
            </button>
          </div>
        </div>

        <div className={styles.right}>
          {result === null ? (
            <div className={styles.awaiting} data-testid="awaiting">
              NOTHING GENERATED YET.
            </div>
          ) : (
            <>
              {/* States the outcome BEFORE showing anything (§10). */}
              <div className={styles.outcome} data-testid="outcome">
                {result.tracks.length} of {result.requested} tracks
              </div>

              <div className={styles.report} data-complete={result.report.binding === null ? "true" : "false"} data-testid="report">
                <div className={styles.reportBand} data-complete={result.report.binding === null ? "true" : "false"} />
                <div className={styles.reportBody}>
                  <div className={styles.reportText} data-testid="report-sentence">
                    {result.sentence}
                  </div>
                  {result.report.relaxed !== null && (
                    <div className={styles.reportActions}>
                      <button type="button" className={styles.reportPrimary} data-testid="relax" onClick={onRelax}>
                        {result.report.binding === "minScore"
                          ? `Lower to ${result.report.relaxed.to}`
                          : result.report.binding === "excludePlayedWithin"
                            ? `Shorten to ${result.report.relaxed.to} days`
                            : `Raise cap to ${result.report.relaxed.to}`}
                      </button>
                      <button type="button" className={styles.reportGhost} data-testid="keep" onClick={onKeep} disabled={saving}>
                        Keep {result.tracks.length}
                      </button>
                    </div>
                  )}
                  {result.report.relaxed === null && (
                    <div className={styles.reportActions}>
                      <button type="button" className={styles.reportPrimary} data-testid="keep" onClick={onKeep} disabled={saving}>
                        {saving ? "Saving…" : `Keep ${result.tracks.length}`}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.tracks} data-testid="generated-tracks">
                {result.tracks.map((track, i) => (
                  <div key={track.id} className={styles.trackRow} data-testid="generated-row">
                    <span className={styles.trackNo}>{i + 1}</span>
                    <span className={styles.trackTitle}>{track.title}</span>
                    <span className={styles.trackArtist}>{track.artist}</span>
                    <span className={styles.trackScore}>{track.score}</span>
                    <span className={styles.trackDur}>{trackLength(track.durationMs)}</span>
                  </div>
                ))}
              </div>

              <div className={styles.total}>{durationLabel(result.durationMs)} TOTAL</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      {children}
    </label>
  );
}

/**
 * A number that can be off.
 *
 * "Off" and "zero" are different instructions — `max 0 per artist` is an empty playlist and
 * `no cap` is no cap — so an empty field means null rather than 0.
 */
function NullableNumber({
  label,
  testId,
  value,
  onChange,
}: {
  label: string;
  testId: string;
  value: number | null;
  onChange: (next: number | null) => void;
}) {
  return (
    <input
      type="number"
      className={styles.number}
      aria-label={label}
      data-testid={testId}
      placeholder="off"
      value={value ?? ""}
      min={1}
      onChange={(event) => onChange(event.target.value === "" ? null : Number(event.target.value))}
    />
  );
}
